/* eslint-disable better-mutation/no-mutation */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    fetchClassificationFromFirstImage,
    fetchPredictionsFromImages,
} from 'tradera/apps/syi/state/actions/predictions-actions';
import { ITEM_CLASSIFICATIONS_FOR_PREDICTION } from 'tradera/apps/syi/state/reducers/images';
import type { ClassifyFromDraftImageApiResponse } from 'tradera/state/services/types/webapi-selling-generated';

const setValidationError = (condition: boolean | undefined) =>
    condition ? 'syi_validation_prediction_publish_consent' : null;

const validate = (state: PredictionsState) => {
    return {
        ...state,
        validationError: setValidationError(!state.hasAutoFillConsent),
    };
};

type PredictionsState = {
    isLoadingPredictions: boolean;
    shortDescription?: string;
    longDescription?: string;
    shouldSuggestShortDescription?: boolean;
    shouldSuggestLongDescription?: boolean;
    numProcessedImages?: number;
    aiProcessingTime?: number;

    classification: ClassifyFromDraftImageApiResponse | null;
    canGetPredictionFromImages: boolean;
    isClassificationPending: boolean;
    hasUsedPredictedDescription: boolean;
    hasAutoFillConsent: boolean;
    validationError?: string | null;
    hasUsedAutoFill: boolean;
};

const initialState: PredictionsState = {
    isLoadingPredictions: false,
    classification: null,
    canGetPredictionFromImages: false,
    isClassificationPending: false,
    hasUsedAutoFill: false,
    hasUsedPredictedDescription: false,
    hasAutoFillConsent: false,
    shortDescription: undefined,
    validationError: null,
};

const predictionsSlice = createSlice({
    name: 'predictions',
    initialState,
    reducers: {
        setPredictedShortDescription: (state, action) => {
            state.shortDescription = action.payload;
        },
        setPredictedLongDescription: (state, action) => {
            state.longDescription = action.payload;
        },
        setShouldSuggestShortDescription: (state, action) => {
            state.shouldSuggestShortDescription = action.payload;
        },
        setShouldSuggestLongDescription: (state, action) => {
            state.shouldSuggestLongDescription = action.payload;
        },
        setHasUsedPredictedDescription: (
            state,
            action: PayloadAction<boolean>,
        ) =>
            validate({
                ...state,
                hasUsedAutoFill: action.payload,
                hasUsedPredictedDescription: action.payload,
                hasAutoFillConsent: false,
            }),
        setHasUsedAutoFill: (state, action: PayloadAction<boolean>) =>
            validate({
                ...state,
                hasUsedAutoFill: action.payload,
            }),
        setNumProcessedImages: (state, action) => {
            state.numProcessedImages = action.payload;
        },
        setAiProcessingTime: (state, action) => {
            state.aiProcessingTime = action.payload;
        },
        setHasAutoFillConsent: (state, action) =>
            validate({
                ...state,
                hasAutoFillConsent: action.payload,
            }),
    },
    extraReducers: (builder) => {
        builder.addCase(fetchClassificationFromFirstImage.pending, (state) => {
            state.classification = null;
            state.isClassificationPending = true;
            state.canGetPredictionFromImages = false;
        });
        builder.addCase(
            fetchClassificationFromFirstImage.fulfilled,
            (state, action) => {
                state.classification = action.payload;
                state.isClassificationPending = false;
                state.canGetPredictionFromImages =
                    action.payload.itemClassification !==
                    ITEM_CLASSIFICATIONS_FOR_PREDICTION.Other;
            },
        );
        builder.addCase(fetchClassificationFromFirstImage.rejected, (state) => {
            state.classification = null;
            state.isClassificationPending = false;
            state.canGetPredictionFromImages = false;
        });
        builder.addCase(fetchPredictionsFromImages.pending, (state) => {
            state.isLoadingPredictions = true;
        });
        builder.addCase(fetchPredictionsFromImages.fulfilled, (state) => {
            state.isLoadingPredictions = false;
        });
        builder.addCase(fetchPredictionsFromImages.rejected, (state) => {
            state.isLoadingPredictions = false;
        });
    },
});

export const {
    setPredictedShortDescription,
    setPredictedLongDescription,
    setShouldSuggestShortDescription,
    setShouldSuggestLongDescription,
    setHasUsedAutoFill,
    setHasUsedPredictedDescription,
    setNumProcessedImages,
    setAiProcessingTime,
    setHasAutoFillConsent,
} = predictionsSlice.actions;
export default predictionsSlice.reducer;
